import React, { useMemo } from 'react'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { FlowGrid, Paragraph } from '@fs/zion-ui'
import { Trans } from 'react-i18next'
import { useLinkablePersons } from '../hooks/useLinkablePersons'
import { getGroupedPersonsSelectionDefaults, FORM_ID } from '../utils/formDefaults'
import GroupedPersonCheckboxes from './GroupedPersonCheckboxes'

const LinkNamesPanelContent = ({ person, field, onSave, linkedPersonIds = null, overlay }) => {
  const { personsInThisImage, personsInThisRecord, personsInOtherImages, personsLinkedToElementOnCurrentImage } =
    useLinkablePersons({
      person,
      field,
      linkedPersonIds,
    })

  const formDefaultValues = useMemo(
    () =>
      getGroupedPersonsSelectionDefaults({
        personIdsSharingElement: personsLinkedToElementOnCurrentImage.map((p) => p.id),
        allTheArksExceptCurrent: personsInOtherImages,
        personsInThisRecord,
        personsInThisImage,
      }),
    [personsLinkedToElementOnCurrentImage, personsInThisRecord, personsInThisImage, personsInOtherImages]
  )

  const methods = useZionForm({
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  const computeLinkChanges = (initialValues, currentValues) => {
    const toLinkSet = new Set()
    const toUnlinkSet = new Set()

    // Combine all sections to check for changes
    const allSections = [
      currentValues.linkStatesInRecord,
      currentValues.linkStatesInImage,
      currentValues.linkStatesInOtherArks,
    ]

    const allInitialSections = [
      initialValues.linkStatesInRecord,
      initialValues.linkStatesInImage,
      initialValues.linkStatesInOtherArks,
    ]

    allSections.forEach((section, sectionIndex) => {
      const initialSection = allInitialSections[sectionIndex]

      Object.entries(section).forEach(([personId, isLinked]) => {
        const wasLinked = initialSection[personId]

        if (isLinked && !wasLinked) {
          toLinkSet.add(personId)
          toUnlinkSet.delete(personId) // Remove from unlink if it appears in multiple sections
        } else if (!isLinked && wasLinked) {
          toUnlinkSet.add(personId)
          toLinkSet.delete(personId) // Remove from link if it appears in multiple sections
        }
      })
    })

    // Count total persons currently linked in this and other images to determine if the field is shared
    const totalLinked = [
      ...Object.values(currentValues.linkStatesInImage),
      ...Object.values(currentValues.linkStatesInOtherArks),
    ].filter(Boolean).length
    const isSharedField = totalLinked > 1

    return {
      toLink: Array.from(toLinkSet),
      toUnlink: Array.from(toUnlinkSet),
      isSharedField,
    }
  }

  const handleSubmit = (formValues) => {
    const changes = computeLinkChanges(formDefaultValues, formValues)
    onSave?.({ ...changes, person, field })
    overlay.close()
  }

  const displayLabel = field?.displayLabel || field?.label
  const displayValue = field?.value

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="sm">
      <Paragraph size="sm">
        <Trans
          i18nKey="linkNamesPanel.instructions"
          values={{ displayLabel, displayValue }}
          components={{ bold: <b /> }}
        >
          Do more names need to be linked to the {{ displayLabel }} <bold>{{ displayValue }}</bold>?
        </Trans>
      </Paragraph>
      <ZionForm methods={methods} onSubmit={handleSubmit} id={FORM_ID}>
        <GroupedPersonCheckboxes
          personsInThisRecord={personsInThisRecord}
          personsInThisImage={personsInThisImage}
          allTheArksExceptCurrent={personsInOtherImages}
          viewInContextHandler={() => {}}
          selectedPersonId={person.id}
        />
      </ZionForm>
    </FlowGrid>
  )
}

export default LinkNamesPanelContent
